import {
    type ReactNode,
    useEffect,
} from 'react';

import { ThemeToggle } from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';

interface SectionProps {
    id: string;
    title: string;
    children: ReactNode;
}

export function Section({ id, title, children }: SectionProps) {
    return (
        <section
            id={id}
            className={cn(
                'scroll-mt-24 border-t border-border/70 pt-10 first:border-t-0 first:pt-0',
            )}
        >
            <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
            <div className="mt-4 space-y-4 text-[0.95rem] leading-relaxed text-muted-foreground">
                {children}
            </div>
        </section>
    );
}

export function SubHeading({ children }: { children: ReactNode }) {
    return (
        <h3 className="!mt-6 text-[0.95rem] font-semibold text-foreground">
            {children}
        </h3>
    );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div className={cn('rounded-2xl border border-border bg-card p-6', className)}>
            {children}
        </div>
    );
}

export function DataTable({ rows }: { rows: { category: string; examples: string }[] }) {
    return (
        <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full border-collapse text-left text-[0.9rem]">
                <thead>
                    <tr className="bg-muted">
                        <th className="px-4 py-2.5 font-semibold text-foreground">Category</th>
                        <th className="px-4 py-2.5 font-semibold text-foreground">Examples</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.category} className="border-t border-border">
                            <td className="px-4 py-2.5 align-top font-medium text-foreground">
                                {row.category}
                            </td>
                            <td className="px-4 py-2.5 align-top text-muted-foreground">
                                {row.examples}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

interface LegalPageShellProps {
    brand: string;
    pageTitle: string;
    documentTitle: string;
    effectiveDate: string;
    lastUpdated: string;
    lead: ReactNode;
    toc: { id: string; title: string }[];
    footerNote: string;
    children: ReactNode;
}

export function LegalPageShell({
    brand,
    pageTitle,
    documentTitle,
    effectiveDate,
    lastUpdated,
    lead,
    toc,
    footerNote,
    children,
}: LegalPageShellProps) {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = documentTitle;
        return () => {
            document.title = previousTitle;
        };
    }, [documentTitle]);

    return (
        <div className="min-h-[100dvh] bg-background text-foreground">
            <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-xl">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5 md:px-8">
                    <span className="text-[1.05rem] font-bold tracking-tight text-foreground">
                        {brand}
                    </span>
                    <ThemeToggle />
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-5 pb-24 pt-10 md:px-8 md:pt-14">
                <div className="mb-10">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                        {pageTitle}
                    </h1>
                    <p className="mt-3 text-sm text-muted-foreground">
                        App:
                        {' '}
                        <span className="font-medium text-foreground">{brand}</span>
                        <span className="mx-2 text-border">·</span>
                        Effective date:
                        {' '}
                        <span className="font-medium text-foreground">{effectiveDate}</span>
                        <span className="mx-2 text-border">·</span>
                        Last updated:
                        {' '}
                        <span className="font-medium text-foreground">{lastUpdated}</span>
                    </p>
                </div>

                <div className="space-y-4 text-[0.95rem] leading-relaxed text-muted-foreground">
                    {lead}
                </div>

                <nav aria-label="Table of contents" className="mt-8">
                    <Card>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Contents
                        </p>
                        <ol className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                            {toc.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        className="text-sm text-primary underline-offset-4 hover:underline"
                                    >
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </Card>
                </nav>

                <div className="mt-14 space-y-10">
                    {children}
                </div>

                <footer className="mt-14 border-t border-border/70 pt-6 text-xs text-muted-foreground">
                    {footerNote}
                </footer>
            </main>
        </div>
    );
}
